"use client";

import type { ReactNode } from "react";
import { toast } from "sonner";

import type { StatusType } from "@/domain/types";

// Ported from dotone-swap-v3's src/context/StatusContext.tsx — now backed
// by shadcn's sonner Toaster (see providers/AppProviders.tsx) instead of
// react-hot-toast. Hook-only: no bare-module-function usage from outside a
// component, unlike the old app's StatusContext.
function showStatus(message: ReactNode, type: StatusType = "info") {
  const duration = type === "info" ? 3000 : 6000;
  if (type === "success") toast.success(message, { duration });
  else if (type === "error") toast.error(message, { duration });
  else toast(message, { duration });
}

function hideStatus() {
  toast.dismiss();
}

// Opens a loading toast that a multi-step transaction can rewrite in place
// (via updateStatus) as it progresses, instead of stacking a new toast per
// step. Returns the id to pass to updateStatus.
function showLoading(message: ReactNode) {
  return toast.loading(message);
}

// Rewrites an existing toast (by id) in place. Ending a sequence with type
// "success"/"error" swaps the spinner for the matching icon on the same
// toast rather than leaving the loading spinner behind.
function updateStatus(id: string | number, message: ReactNode, type: StatusType = "info") {
  const duration = type === "info" ? 3000 : 10000;
  if (type === "success") toast.success(message, { id, duration });
  else if (type === "error") toast.error(message, { id, duration });
  else toast.loading(message, { id });
}

export function useStatus() {
  return { showStatus, hideStatus, showLoading, updateStatus };
}
